import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { MAX_OF_DISPLAY_FORECAST_MONTH, MIN_OF_TARGET_END_MONTH_AGO, MIN_OF_TARGET_START_MONTH_AGO } from '../constants';

/**
 * 仕入先内示の全取引ページで使用するロジックをまとめる
 */
export class AllBusinessesForSupplierForecastViewModel {
  /**
   * 対象の終了年月に指定可能な年月の範囲を取得する
   * @param startPeriod - 開始年月
   */
  private _getEndPeriodRange = (startPeriod: IYearMonth): [start: IYearMonth, end: IYearMonth] => {
    // 開始年月の翌月以降である必要があるため+1
    const { updateYear: nextYear, updateMonth: nextMonth } = DateService.updateYearMonth(startPeriod.year, startPeriod.month, 1);
    let startRangeYearMonth = { year: nextYear, month: nextMonth };

    const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
    // 最小値を下回っていた場合は最小値に補正
    const { updateYear: minYear, updateMonth: minMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      MIN_OF_TARGET_END_MONTH_AGO
    );
    if (DateService.compareYearMonths(startRangeYearMonth, { year: minYear, month: minMonth }) < 0) {
      startRangeYearMonth = { year: minYear, month: minMonth };
    }
    // 当月から最大表示月数分の年月進めたものを終了年月とする
    const { updateYear: endRangeYear, updateMonth: endRangeMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      MAX_OF_DISPLAY_FORECAST_MONTH
    );
    return [startRangeYearMonth, { year: endRangeYear, month: endRangeMonth }];
  };
  /**
   * 仕入先内示ダウンロードモーダルの対象終了年月の選択可能な年月のリストを取得する。
   * 選択肢なので、YYYY/MM形式の文字列で返す
   * @param startPeriod - 開始年月
   */
  public getEndPeriodOptionsDownloadModal = (startPeriod: IYearMonth): string[] => {
    return DateService.getYearMonthStringList(...this._getEndPeriodRange(startPeriod));
  };

  /**
   * 仕入先内示ダウンロードモーダルの対象終了年月のラベルを取得する
   * @param endPeriod - 終了年月
   */
  public getEndPeriodLabelDownloadModal = (endPeriod: IYearMonth): string => {
    return DateService.convertIYearMonthToYYYYMMWithSlash(endPeriod, true);
  };

  /**
   * 年月リストから指定したインデックスの年月を取得する
   * @param index - インデックス
   * @param startPeriod - 開始年月
   */
  public getValueByTargetYearMonthList = (index: number, startPeriod: IYearMonth): IYearMonth => {
    // まず開始年月と最大表示月数から年月リストを作成する
    const yearMonthList = DateService.getYearMonthsBetween(...this._getEndPeriodRange(startPeriod));
    // インデックスに対応する年月を取得する
    return yearMonthList[index];
  };

  /**
   * 開始年月が無効な範囲かどうかを判定する
   * @param startPeriod - 開始年月
   */
  public isInvalidStartPeriod = (startPeriod: IYearMonth): boolean => {
    // 開始年月の有効範囲は「当月の60ヶ月前」〜当月までが有効。それ以外は無効
    const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
    const { updateYear: validYear, updateMonth: validMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      MIN_OF_TARGET_START_MONTH_AGO
    );
    return (
      DateService.compareYearMonths(startPeriod, { year: validYear, month: validMonth }) < 0 ||
      DateService.compareYearMonths(startPeriod, currentYearMonth) > 0
    );
  };
}
